import Layout from "../../components/Layout";
import { FormSkeleton } from "@/components/FormSkeleton";
import { Heading } from "@/components/typography";
import { Card, CardContent } from "@/components/ui/card";
import { useSkeletonPhases } from "@/hooks/useSkeletonPhases";
import type { CampaignFormMode } from "./campaign";
import { BriefSection } from "./components/BriefSection";
import { CampaignBasicsSection } from "./components/CampaignBasicsSection";
import { EligibilitySection } from "./components/EligibilitySection";
import { ExtrasSection } from "./components/ExtrasSection";
import { CampaignFormShell } from "./components/CampaignFormShell";
import { useCampaignForm } from "./useCampaignForm";

type CampaignFormProps = {
    mode: CampaignFormMode;
};

const CAMPAIGN_FORM_SKELETON_SECTIONS = [
    { id: "campaign-basics", title: "Campaign Basics", fields: ["input", "input", "input", "input", "input"] },
    { id: "brief", title: "Brief", fields: ["input", "input", "input", "textarea", "input"] },
    { id: "eligibility", title: "Creator Eligibility", fields: ["input", "input", "input", "input"] },
    { id: "extras", title: "Extras", fields: ["input", "input", "input", "input"] },
] as const;

export default function CampaignForm({ mode }: CampaignFormProps) {
    const form = useCampaignForm(mode);
    const phase = useSkeletonPhases(form.loading);

    return (
        <Layout gray>
            <CampaignFormShell {...form.shell} disabled={form.shell.isSubmitting || phase === "showSkeleton"}>
                {phase === "showSkeleton" ? (
                    <CampaignFormSectionsSkeleton />
                ) : phase === "showResult" && (
                    <>
                        <CampaignBasicsSection {...form.basics} />
                        <BriefSection {...form.brief} />
                        <EligibilitySection {...form.eligibility} />
                        <ExtrasSection {...form.extras} />
                    </>
                )}
            </CampaignFormShell>
        </Layout>
    );
}

/** Section titles plus compact FormSkeleton bars for an edit-mode campaign-doc wait. */
function CampaignFormSectionsSkeleton() {
    return (
        <>
            {CAMPAIGN_FORM_SKELETON_SECTIONS.map((section) => (
                <section key={section.id} id={section.id} className="scroll-mt-16">
                    <Heading type="h4" className="mb-4 font-bold">
                        {section.title}
                    </Heading>
                    <Card size="sm">
                        <CardContent>
                            <FormSkeleton fields={[...section.fields]} />
                        </CardContent>
                    </Card>
                </section>
            ))}
        </>
    );
}
